import { Button, Dialog, DialogContent, DialogTitle, Divider } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { SnackBarContext } from 'src/App'
import Spacer from 'src/common/components/Spacer'
import { OktaLogo } from 'src/common/logo/okta'
import settingsService from 'src/services/settings.service'
import CustomProviders from './CustomProviders/CustomProviders'

const ConnectorIcon = ({ type }) => {
  if (type == 'OKTA_SSO') {
    return <OktaLogo />
  }
  if (type == 'GOOGLE_SSO') {
    return (
      <svg width='32' height='32' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M29.403 15.3598C29.403 14.3467 29.305 13.301 29.1416 12.3206H14.9915V18.1048H23.096C22.7692 19.9675 21.6908 21.6015 20.0895 22.6472L24.926 26.4053C27.7691 23.7583 29.403 19.9022 29.403 15.3598Z'
          fill='#4280EF'
        />
        <path
          d='M14.9915 30C19.0437 30 22.4424 28.6602 24.926 26.3726L20.0895 22.6472C18.7496 23.5622 17.0176 24.0851 14.9915 24.0851C11.07 24.0851 7.76946 21.4381 6.56033 17.9088L1.59311 21.7322C4.14208 26.7975 9.30538 30 14.9915 30Z'
          fill='#34A353'
        />
        <path
          d='M6.56033 17.876C5.93942 16.0133 5.93942 13.9872 6.56033 12.1245L1.59311 8.26837C-0.531035 12.5167 -0.531035 17.5166 1.59311 21.7322L6.56033 17.876Z'
          fill='#F6B704'
        />
        <path
          d='M14.9915 5.94817C17.1157 5.91549 19.2071 6.73247 20.7431 8.20303L25.024 3.88938C22.3117 1.34041 18.717 -0.0321088 14.9915 0.000570345C9.30538 0.000570345 4.14208 3.20312 1.59311 8.26839L6.56033 12.1245C7.76946 8.5625 11.07 5.94817 14.9915 5.94817Z'
          fill='#E54335'
        />
      </svg>
    )
  }
  if (type == 'OUTLOOK_SSO') {
    return (
      <svg width={'35'} height={'32'} viewBox='0 0 35 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M2.13887 18.3415L17.5 28.2927L33.0555 18.1464L35 20.4878L17.5 32L-1.52588e-05 20.4878L2.13887 18.3415Z'
          fill='#50E6FF'
        />
        <path d='M3.30556 16.9756L17.5 0L31.8889 16.9756L17.5 26.1463L3.30556 16.9756Z' fill='white' />
        <path d='M17.5 0V26.1463L3.30556 16.9756L17.5 0Z' fill='#50E6FF' />
        <path d='M17.5 0V26.1463L31.8889 16.9756L17.5 0Z' fill='url(#paint0_linear_782_126044)' />
        <path d='M17.5 13.4634L31.8889 16.9756L17.5 26.1463V13.4634Z' fill='#53B1E0' />
        <path d='M17.5 26.1463L3.30556 16.9756L17.5 13.4634V26.1463Z' fill='#9CEBFF' />
        <path d='M17.5 32L35 20.4878L33.0555 18.1464L17.5 28.2927V32Z' fill='url(#paint1_linear_782_126044)' />
        <defs>
          <linearGradient
            id='paint0_linear_782_126044'
            x1='25.8599'
            y1='23.7954'
            x2='16.8081'
            y2='6.70927'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#1988D9' />
            <stop offset='0.9' stopColor='#54AEF0' />
          </linearGradient>
          <linearGradient
            id='paint1_linear_782_126044'
            x1='21.9839'
            y1='18.8232'
            x2='28.2385'
            y2='29.5751'
            gradientUnits='userSpaceOnUse'
          >
            <stop offset='0.1' stopColor='#54AEF0' />
            <stop offset='0.29' stopColor='#4FABEE' />
            <stop offset='0.51' stopColor='#41A2E9' />
            <stop offset='0.74' stopColor='#2A93E0' />
            <stop offset='0.88' stopColor='#1988D9' />
          </linearGradient>
        </defs>
      </svg>
    )
  }
}

const connectorsLocal = [
  {
    title: 'Okta',
    connectorType: 'OKTA_SSO',
    availableToUpdate: false
  }
]
const connectorsLocalSecond = [
  {
    title: 'Google',
    connectorType: 'GOOGLE_SSO',
    availableToUpdate: false
  },
  {
    title: 'Microsoft Entra ID',
    connectorType: 'OUTLOOK_SSO',
    availableToUpdate: false
  }
]

export const SsoSettings = () => {
  dayjs.extend(utc)

  const [open, setOpen] = useState(false)
  const [selectedConnector, setSelectedConnector] = useState(null)
  const [connectorsSecond, setConnectorsSecond] = useState([])

  const { setSuccessMessage, setErrorMessage } = useContext(SnackBarContext)

  const onClose = () => {
    setOpen(false)
  }

  const onAction = () => {
    settingsService
      .disconnect(selectedConnector)
      .then(() => {
        updateConnectors()
        setOpen(false)
        setSuccessMessage('Provider disconnected')
      })
      .catch(err => {
        console.error(err)
        setErrorMessage('Something went wrong')
      })
  }

  const { connectors, query, handleConnectTo, updateConnectors, setUpdadeDialog, loading, setLoading } =
    useOutletContext()

  function disconnect(connector) {
    setOpen(true)
    setSelectedConnector(connector)
    if (connector == 'AD') {
      setUpdadeDialog(false)
    }
  }

  const updateStatus = (type, status) => {
    setLoading(true)
    settingsService
      .setConnectorsSecond(type, status)
      .then(() => {
        settingsService
          .getConnectorsSecond()
          .then(resp => {
            setLoading(false)
            setConnectorsSecond(resp.data)
          })
          .catch(e => {
            console.error(e)
            setLoading(false)
          })
      })
      .catch(e => {
        setLoading(false)
        console.error(e)
      })
  }

  useEffect(() => {
    settingsService
      .getConnectorsSecond()
      .then(resp => {
        setConnectorsSecond(resp.data)
      })
      .catch(e => console.error(e))
  }, [])

  return (
    <div className='settings-section'>
      <h2 id='integrations-section'>SSO Settings</h2>
      <p style={{ maxWidth: '730px', lineHeight: '24px' }}>Connect Cyberlift to your external platform</p>
      <div
        style={{
          marginTop: '20px',
          flexWrap: 'wrap',
          width: '100%',
          marginBottom: 20
        }}
        className='row'
      >
        {connectorsLocal?.map(el => {
          return (
            <div
              className='column'
              key={el.connectorType}
              style={{
                alignItems: 'flex-start',
                border:
                  query.get('errorSync') && query.get('errorSyncType') == el.connectorType
                    ? '1px solid #B40020'
                    : '1px solid #E7E7EE',
                padding: '17px 21px',
                width: 322,
                borderRadius: 14,
                marginRight: 14,
                marginBottom: 16,
                height: 180
              }}
            >
              <div style={{ marginBottom: 7 }}>
                <ConnectorIcon type={el.connectorType} />
              </div>

              <p style={{ fontWeight: 600 }}>{el.title}</p>

              {connectors?.map(connector => connector.provider).includes(el.connectorType) && (
                <>
                  <div
                    style={{
                      color: '#00BC62',
                      marginTop: 10
                    }}
                    key={el.connectorType}
                    className='row row-center'
                  >
                    <p>Active</p>
                    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M13.5 4.5L6.5 11.4997L3 8'
                        stroke='#00BC62'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </>
              )}
              {query.get('errorSync') && query.get('errorSyncType') == el.connectorType && (
                <p className='error-text' style={{ marginTop: 20, marginLeft: 0 }}>
                  {query.get('errorSync')}
                </p>
              )}
              <Spacer />
              {connectors?.map(connector => connector.provider).includes(el.connectorType) ? (
                <div className='row' style={{ width: '100%' }}>
                  <Button
                    key={el.connectorType + 'btn'}
                    sx={{
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '15px',
                      color: '#6123FF',
                      textTransform: 'none',
                      padding: 0,
                      mr: '20px'
                    }}
                    onClick={() => disconnect(el.connectorType)}
                  >
                    Disable
                  </Button>
                </div>
              ) : (
                <Button
                  key={el.connectorType + 'btn'}
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '15px',
                    color: '#6123FF',
                    textTransform: 'none',
                    padding: 0
                  }}
                  onClick={() => handleConnectTo(el.connectorType)}
                >
                  Enable
                </Button>
              )}
            </div>
          )
        })}

        {connectorsLocalSecond?.map(el => {
          return (
            <div
              className='column'
              key={el.connectorType}
              style={{
                alignItems: 'flex-start',
                border:
                  query.get('errorSync') && query.get('errorSyncType') == el.connectorType
                    ? '1px solid #B40020'
                    : '1px solid #E7E7EE',
                padding: '17px 21px',
                width: 322,
                borderRadius: 14,
                marginRight: 14,
                marginBottom: 16,
                height: 180
              }}
            >
              <div style={{ marginBottom: 7 }}>
                <ConnectorIcon type={el.connectorType} />
              </div>

              <p style={{ fontWeight: 600 }}>{el.title}</p>

              {connectorsSecond?.map(connector => connector.ssoProvider).includes(el.connectorType) && (
                <>
                  <div
                    style={{
                      color: '#00BC62',
                      marginTop: 10
                    }}
                    key={el.connectorType}
                    className='row row-center'
                  >
                    <p>Active</p>
                    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M13.5 4.5L6.5 11.4997L3 8'
                        stroke='#00BC62'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </>
              )}
              {query.get('errorSync') && query.get('errorSyncType') == el.connectorType && (
                <p className='error-text' style={{ marginTop: 20, marginLeft: 0 }}>
                  {query.get('errorSync')}
                </p>
              )}
              <Spacer />
              {connectorsSecond?.map(connector => connector.ssoProvider).includes(el.connectorType) ? (
                <div className='row' style={{ width: '100%' }}>
                  <Button
                    disabled={loading}
                    key={el.connectorType + 'btn'}
                    sx={{
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '15px',
                      color: '#6123FF',
                      textTransform: 'none',
                      padding: 0,
                      mr: '20px'
                    }}
                    onClick={() => updateStatus(el.connectorType, false)}
                  >
                    Disable
                  </Button>
                </div>
              ) : (
                <Button
                  disabled={loading}
                  key={el.connectorType + 'btn'}
                  sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '15px',
                    color: '#6123FF',
                    textTransform: 'none',
                    padding: 0
                  }}
                  onClick={() => updateStatus(el.connectorType, true)}
                >
                  Enable
                </Button>
              )}
            </div>
          )
        })}
      </div>
      <Divider sx={{ marginBottom: '20px', marginTop: '20px', width: '100%' }} />
      <CustomProviders />
      <Dialog
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '22px',
            paddingTop: '8px',
            maxWidth: 'unset',
            width: '684px'
          }
        }}
        open={open}
        onClose={onClose}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Satoshi',
            fontStyle: 'normal',
            fontWeight: '900',
            fontSize: '35px',
            lineHeight: '110%',
            color: '#1B1B26'
          }}
        >
          Disconnect?
        </DialogTitle>
        <DialogContent>
          <p
            style={{
              fontSize: '14px',
              marginBottom: '80px',
              lineHeight: '22px'
            }}
          >
            Are you sure you want to disconnect this provider?
          </p>
          <div className='row'>
            <Button
              sx={{
                padding: '10px 0px',
                fontFamily: 'Plus Jakarta Sans',
                textTransform: 'none',
                borderRadius: '8px',
                borderColor: '#D0D0D8',
                color: '#1B1B26'
              }}
              variant={'outlined'}
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <div style={{ width: '20px' }}></div>
            <Button
              onClick={onAction}
              sx={{
                padding: '10px 0px',
                fontFamily: 'Plus Jakarta Sans',
                textTransform: 'none',
                borderRadius: '8px',
                background: '#6123FF'
              }}
              variant={'contained'}
              fullWidth
            >
              Yes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
